// ---------------------------------------------------------------------------
// Row-building for CalculationResults.tsx's 4 result-level tables (sub-basin/
// outlet point/zone/aquifer) — pure data transformation (SubBasinResult[]/
// AggregatedContribution[] -> the exact rows each table renders), no React.
// Split out of CalculationResults.tsx (see project_evalhid_code_audit.md's
// ongoing "large file" pass) since none of this needs the component's own
// hooks/state to run or to be tested.
// ---------------------------------------------------------------------------
import type { AggregatedContribution, AquiferContribution } from "../engine/aggregation.ts";
import type { SubBasinResult } from "../engine/engine.ts";
import type { Granularity } from "../model/aggregates.ts";
import type { EvalHidProject } from "../model/types.ts";

interface SubBasinRow {
  id: number;
  name: string;
  area: number;
  /** Total mm divided by the raw step count — a genuine DAILY average only when the project's own seriesScale is "daily"; for a monthly-scale project this is really a monthly average (see CalculationResults.tsx's own label, which reads project.seriesScale to say the right thing). */
  averagePerStepMm: number;
  hm3PerPeriod: number;
}

/**
 * `divisor` comes from `periodDivisor` below, at whichever granularity the
 * results panel is currently showing — NOT always
 * `results[i].contribution.length` (that's the raw daily step count, only
 * the right divisor for "daily" itself).
 */
export function buildSubBasinRows(
  results: SubBasinResult[],
  project: EvalHidProject,
  divisor: number,
  coefficient: number
): SubBasinRow[] {
  const areaBySubBasin = new Map(project.subBasins.map((c) => [c.subBasinCode, c.area]));
  return results
    .map((r) => {
      const area = areaBySubBasin.get(r.subBasinCode) ?? 0;
      // NaN marks a step past this sub-basin's own truncation point
      // (engine.ts's padToFullLength) — treated as 0 here, same convention
      // aggregateByKey (aggregation.ts) and groupByPeriod (aggregates.ts)
      // already use, rather than letting it NaN-poison the whole total.
      const totalMm = r.contribution.reduce((acc, v) => acc + (Number.isFinite(v) ? v : 0), 0);
      const hm3Total = totalMm * area * coefficient;
      return {
        id: r.subBasinCode,
        name: r.description,
        area,
        averagePerStepMm: totalMm / r.contribution.length,
        hm3PerPeriod: divisor > 0 ? hm3Total / divisor : 0,
      };
    })
    .sort((a, b) => b.hm3PerPeriod - a.hm3PerPeriod);
}

/**
 * How many periods to divide a total by, for the chosen granularity —
 * `years` (fractional elapsed years, `yearsInSeries`, App.tsx) both IS the
 * "annual" case directly (unchanged from before this granularity selector
 * existed, so the default view's numbers stay byte-for-byte the same as
 * the regression reference) and scales to "monthly" (`years * 12`, same
 * elapsed-time-fraction spirit, not a distinct-calendar-month COUNT — a
 * range starting/ending mid-month would make a count-based divisor
 * over/undercount by one). "daily" uses the real step count instead
 * (`dates.length`, always a whole gapless daily axis — see dailySeries.ts),
 * same value `averagePerStepMm` above already used.
 */
export function periodDivisor(granularity: Granularity, dates: string[], years: number): number {
  switch (granularity) {
    case "daily":
      return dates.length;
    case "monthly":
      return years * 12;
    case "annual":
      return years;
  }
}

export interface AggregatedRow {
  id: number;
  name: string;
  hm3PerPeriod: number;
  hm3SurfacePerPeriod: number;
  hm3GroundwaterPerPeriod: number;
  hm3UnbrokenDownPerPeriod: number;
}

function sumPerPeriod(series: Float64Array, divisor: number): number {
  const total = series.reduce((acc, v) => acc + v, 0);
  return divisor > 0 ? total / divisor : 0;
}

export function buildAggregatedRows<T extends AggregatedContribution>(
  aggregated: T[],
  idOf: (a: T) => number,
  nameOf: (a: T) => string,
  divisor: number
): AggregatedRow[] {
  return aggregated
    .map((a) => ({
      id: idOf(a),
      name: nameOf(a),
      hm3PerPeriod: sumPerPeriod(a.volume, divisor),
      hm3SurfacePerPeriod: sumPerPeriod(a.surfaceVolume, divisor),
      hm3GroundwaterPerPeriod: sumPerPeriod(a.groundwaterVolume, divisor),
      hm3UnbrokenDownPerPeriod: sumPerPeriod(a.noBreakdownVolume, divisor),
    }))
    .sort((a, b) => b.hm3PerPeriod - a.hm3PerPeriod);
}

/**
 * The aquifer's own row shape — different from AggregatedRow's (no sup/
 * subt breakdown: an aquifer has no surface component of its own). Matches
 * Sal_Acuiferos.csv's real flow columns instead (`_Rec`/`_Bomb`/`_Qsal` —
 * `_Vol`, the storage STATE rather than a period flow, isn't summable the
 * same way, so it isn't shown in this table).
 */
export interface AquiferRow {
  id: number;
  name: string;
  hm3DischargePerPeriod: number;
  hm3InfiltrationPerPeriod: number;
  hm3PumpingPerPeriod: number;
}

export function buildAquiferRows(aggregated: AquiferContribution[], divisor: number): AquiferRow[] {
  return aggregated
    .map((a) => ({
      id: a.aquiferCode,
      name: a.name,
      hm3DischargePerPeriod: sumPerPeriod(a.discharge, divisor),
      hm3InfiltrationPerPeriod: sumPerPeriod(a.infiltration, divisor),
      hm3PumpingPerPeriod: sumPerPeriod(a.pumping, divisor),
    }))
    .sort((a, b) => b.hm3DischargePerPeriod - a.hm3DischargePerPeriod);
}
